/**
 * Overview: every website side by side, shown when "All websites" is chosen.
 *
 * Built entirely from the per-bot endpoints the dashboard already uses — one
 * analytics call and a paged conversation list per bot — so it needs nothing
 * new from the backend. The analytics endpoint has no message count, so
 * messages are summed from the conversation list instead; at this volume that
 * is a handful of requests, but it is the first thing to move server-side if
 * traffic grows into the thousands of conversations.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { api } from '../api/client';
import type { AnalyticsResponse, BotSummary, StatDelta } from '../api/types';
import { Card, ErrorNotice, PageHeader, Spinner } from '../components/ui';
import { useBots } from '../hooks/useBots';
import { siteHost } from '../lib/format';
import { RANGES, StatTile, isoDaysAgo, today } from './Analytics';

/** Same hue as "Total conversations" on the Analytics page. */
const LINE = '#2a78d6';

/** The conversation list endpoint's largest page. */
const PAGE_SIZE = 200;

interface SiteStats {
  bot: BotSummary;
  analytics: AnalyticsResponse;
  messages: number;
}

/** Sum messages in conversations that started between `start` and `end`. */
async function countMessages(
  slug: string,
  start: string,
  end: string,
): Promise<number> {
  let total = 0;
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const page = await api.listConversations(slug, { limit: PAGE_SIZE, offset });
    for (const item of page.items) {
      const day = item.started_at.slice(0, 10);
      if (day >= start && day <= end) total += item.message_count;
    }
    if (offset + PAGE_SIZE >= page.total || page.items.length === 0) return total;
  }
}

/** Add StatDeltas together; the percentage is recomputed from the sums. */
function sumStats(stats: StatDelta[]): StatDelta {
  const value = stats.reduce((sum, stat) => sum + stat.value, 0);
  const previous = stats.reduce((sum, stat) => sum + stat.previous, 0);
  const change = value - previous;
  return {
    value,
    previous,
    change,
    percent: previous === 0 ? null : Math.round((change / previous) * 100),
  };
}

export function Overview(): JSX.Element {
  const { bots, select } = useBots();
  const navigate = useNavigate();

  const [start, setStart] = useState(() => isoDaysAgo(30));
  const [end, setEnd] = useState(today);
  const [sites, setSites] = useState<SiteStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setSites(null);
    try {
      setSites(
        await Promise.all(
          bots.map(async (bot) => ({
            bot,
            analytics: await api.analytics(bot.slug, start, end),
            messages: await countMessages(bot.slug, start, end),
          })),
        ),
      );
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not load the overview',
      );
      setSites([]);
    }
  }, [bots, start, end]);

  useEffect(() => {
    void load();
  }, [load]);

  function open(slug: string): void {
    select(slug);
    navigate('/analytics');
  }

  const total = (pick: (site: SiteStats) => StatDelta): StatDelta =>
    sumStats((sites ?? []).map(pick));

  return (
    <div>
      <PageHeader
        title="All websites"
        description={`${bots.length} websites · ${start} to ${end}`}
      />

      {error && <ErrorNotice message={error} />}

      <Card className="mb-4 flex flex-wrap items-center gap-2 p-3">
        {RANGES.map((range) => (
          <button
            key={range.days}
            type="button"
            onClick={() => {
              setStart(isoDaysAgo(range.days));
              setEnd(today());
            }}
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              start === isoDaysAgo(range.days) && end === today()
                ? 'bg-brand-navy text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {range.label}
          </button>
        ))}
      </Card>

      {!sites ? (
        <Spinner label="Loading every website…" />
      ) : sites.length > 0 ? (
        <>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            All websites combined
          </h2>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Conversations"
              stat={total((s) => s.analytics.conversations)}
            />
            <Card className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Messages</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                {sites.reduce((sum, site) => sum + site.messages, 0).toLocaleString()}
              </p>
              <p className="mt-0.5 text-xs text-slate-400">
                Visitor and AI messages, both counted
              </p>
            </Card>
            <StatTile
              label="Contacts captured"
              stat={total((s) => s.analytics.contacts_captured)}
            />
            <StatTile
              label="Leads recommended"
              stat={total((s) => s.analytics.leads_recommended)}
            />
          </div>

          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Each website
          </h2>
          <div className="grid gap-4 lg:grid-cols-3">
            {sites.map((site) => (
              <SiteCard
                key={site.bot.slug}
                site={site}
                onOpen={() => open(site.bot.slug)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function SiteCard({
  site,
  onOpen,
}: {
  site: SiteStats;
  onOpen: () => void;
}): JSX.Element {
  const { analytics } = site;
  const conversations = analytics.conversations;
  const resolvedShare =
    conversations.value === 0
      ? null
      : Math.round((analytics.ai_resolved.value / conversations.value) * 100);

  const rows: [string, string][] = [
    ['Messages', site.messages.toLocaleString()],
    [
      'AI resolved',
      `${analytics.ai_resolved.value.toLocaleString()}${
        resolvedShare === null ? '' : ` (${resolvedShare}%)`
      }`,
    ],
    ['Human involved', analytics.human_involved.value.toLocaleString()],
    ['Contacts captured', analytics.contacts_captured.value.toLocaleString()],
    ['Leads recommended', analytics.leads_recommended.value.toLocaleString()],
    [
      'Feedback',
      `👍 ${analytics.positive_feedback.value} · 👎 ${analytics.negative_feedback.value}`,
    ],
  ];

  return (
    <Card className="flex flex-col p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="truncate text-base font-semibold text-slate-900">
          {siteHost(site.bot.site_url)}
        </h3>
        {!site.bot.is_active && (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
            inactive
          </span>
        )}
      </div>
      <p className="truncate text-xs text-slate-500">Assistant: {site.bot.name}</p>

      <p className="mt-3 text-xs uppercase tracking-wide text-slate-500">
        Conversations
      </p>
      <p className="text-3xl font-semibold tabular-nums text-slate-900">
        {conversations.value.toLocaleString()}
      </p>
      <p className="text-xs text-slate-500">
        {conversations.change === 0
          ? 'No change'
          : `${conversations.change > 0 ? '+' : ''}${conversations.change}${
              conversations.percent === null ? '' : ` (${conversations.percent}%)`
            }`}{' '}
        <span className="text-slate-400">vs previous period</span>
      </p>

      <div className="mt-2 h-16" aria-label="Conversations per day">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={analytics.daily}
            margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id={`spark-${site.bot.slug}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LINE} stopOpacity={0.12} />
                <stop offset="100%" stopColor={LINE} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="day" hide />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Area
              type="monotone"
              dataKey="conversations"
              name="Conversations"
              stroke={LINE}
              strokeWidth={2}
              fill={`url(#spark-${site.bot.slug})`}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <dl className="mt-3 divide-y divide-slate-100 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between py-1.5">
            <dt className="text-slate-500">{label}</dt>
            <dd className="tabular-nums text-slate-800">{value}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={onOpen}
        className="mt-4 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-brand-blue hover:bg-brand-light"
      >
        Open {site.bot.name} ({siteHost(site.bot.site_url)}) →
      </button>
    </Card>
  );
}
